/* ACCESS MANAGEMENT — the Founder's own people surface.
   Manage who can open the Founder Console and what they can see.

   Every decision here is made by the server: this file shows what the server allows and asks it
   to act. It computes no authority of its own, and it holds no copy of the approved company
   domain, the role bundles or the permissions — all three arrive in the payload. */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ShieldCheck, UserPlus, Trash2, History, Lock, Search, X, Check, Minus, Users2,
  ChevronRight, ChevronLeft, Pencil, Eye,
} from "lucide-react";
import { Panel, Chip, Note, DASH } from "./ConsolePrimitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/founder`;
const PER_PAGE = 10;

/* Presentation only: a short name for each permission the server sent. The authority is the
   server's own list; this is how it reads on a screen instead of as an identifier. */
const ABILITY = {
  "console.overview": "Console overview",
  "console.members": "Member counts and growth",
  "console.relationship": "Relationship and adoption",
  "console.usage": "Usage and allowance pressure",
  "console.radar": "Capability Radar",
  "console.radar.dispose": "Set Radar dispositions",
  "console.feedback": "Explicit feedback (aggregate)",
  "console.system": "Operational health",
  "console.brief": "Founder Brief",
  "console.metrics": "Metric provenance",
  "console.access": "Access management",
};

const initials = (name, email) => {
  const source = (name || "").trim() || (email || "").split("@")[0].replace(/[._-]/g, " ");
  const parts = source.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

const Modal = ({ title, onClose, children, testId }) => (
  <div className="fc-modal" data-testid={testId} onMouseDown={onClose}>
    <div className="fc-modal__card" onMouseDown={(e) => e.stopPropagation()}>
      <div className="fc-modal__head">
        <h3>{title}</h3>
        <button className="fc-modal__x" onClick={onClose} data-testid={`${testId}-close`}>
          <X style={{ width: 16, height: 16 }} />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const RoleCards = ({ roles, assignable, value, onPick, disabled, testId }) => (
  <div className="fc-roleset" data-testid={testId}>
    {assignable.map((r) => (
      <button
        key={r} className="fc-roleset__opt" data-active={value === r} disabled={disabled}
        data-testid={`${testId}-${r}`} onClick={() => onPick(r)}
      >
        <span>{roles[r]?.label || r}</span>
        <i>{roles[r]?.description}</i>
      </button>
    ))}
  </div>
);

/* What the selected role can and cannot open — in words, from the server's own bundle. */
const Abilities = ({ permissions, granted, testId }) => (
  <div className="fc-abilities" data-testid={testId}>
    {Object.keys(permissions || {}).map((key) => {
      const has = (granted || []).includes(key);
      return (
        <span key={key} className="fc-abilities__row" data-has={has}
          title={permissions[key]}>
          {has ? <Check /> : <Minus />} {ABILITY[key] || key}
        </span>
      );
    })}
    <span className="fc-abilities__row" data-has={false} data-never="true">
      <Minus /> Private member content — never, for any role
    </span>
  </div>
);

const statusTone = (status) =>
  status === "active" ? "good" : status === "revoked" ? "warn" : "quiet";

export const AccessView = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("analyst");
  const [editing, setEditing] = useState(null);
  const [editRole, setEditRole] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [showRoles, setShowRoles] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/access`);
    setData(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setBusy(true); setMessage(null);
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setMessage(e?.response?.data?.detail || "That change was refused.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const rows = data?.access || [];
  const counts = useMemo(() => ({
    all: rows.length,
    active: rows.filter((r) => r.status === "active").length,
    revoked: rows.filter((r) => r.status === "revoked").length,
  }), [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => (filter === "all" || r.status === filter)
      && (!q || r.email.includes(q) || (r.name || "").toLowerCase().includes(q)));
  }, [rows, query, filter]);

  const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, pages - 1);
  const visible = filtered.slice(current * PER_PAGE, current * PER_PAGE + PER_PAGE);

  if (!data) {
    return <div className="fc-unavail" data-testid="fc-access-loading">Reading the access record…</div>;
  }

  const assignable = data.assignable_roles || [];
  const domains = data.company_domains || [];
  const editingRow = rows.find((r) => r.email === editing);
  const revokingRow = rows.find((r) => r.email === revoking);

  return (
    <>
      <div className="fc-people__bar">
        <button className="fc-btn fc-btn--lg" data-testid="fc-access-add"
          onClick={() => { setMessage(null); setEmail(""); setRole(assignable[0] || "analyst"); setAdding(true); }}>
          <UserPlus style={{ width: 15, height: 15 }} /> Add person
        </button>
        <label className="fc-search">
          <Search style={{ width: 14, height: 14, opacity: 0.5 }} />
          <input
            placeholder="Search by name or email…" value={query} data-testid="fc-access-search"
            onChange={(e) => { setQuery(e.target.value); setPage(0); }}
          />
        </label>
        <div className="fc-filters">
          {[["all", "All"], ["active", "Active"], ["revoked", "Revoked"]].map(([key, label]) => (
            <button
              key={key} className="fc-filter" data-active={filter === key}
              data-testid={`fc-access-filter-${key}`}
              onClick={() => { setFilter(key); setPage(0); }}
            >
              {label} ({counts[key]})
            </button>
          ))}
        </div>
      </div>

      <Panel title="Who can open the Founder Console" icon={Users2} testId="fc-access-panel"
        sub="Individually authenticated internal accounts, and exactly what each one may see.">
        <div className="fc-people__head">
          <span>Person</span><span>Role</span><span>Status</span><span>Authorized by</span>
          <span>Manage</span>
        </div>

        {visible.length === 0 && (
          <div className="fc-unavail" data-testid="fc-access-empty">
            No authorized identity matches that.
          </div>
        )}

        {visible.map((row) => {
          const spec = data.roles[row.role] || {};
          const owner = row.role === "owner";
          const bootstrap = owner && row.email === data.bootstrap?.email
            && data.bootstrap?.retireable;
          return (
            <div className="fc-people__row" key={row.email}
              data-testid={`fc-access-row-${row.email}`}>
              <div className="fc-people__who">
                <span className="fc-avatar">{initials(row.name, row.email)}</span>
                <span style={{ minWidth: 0 }}>
                  <b>{row.name || row.email}</b>
                  {row.name ? <i>{row.email}</i> : null}
                </span>
              </div>
              <div className="fc-people__role">
                {spec.label || row.role}
                <i data-testid={`fc-access-perms-${row.email}`}>
                  {row.status === "revoked"
                    ? "No permissions. Refused at every Console request."
                    : spec.description}
                </i>
              </div>
              <div>
                <Chip tone={statusTone(row.status)} testId={`fc-access-status-${row.email}`}>
                  {row.status}
                </Chip>
              </div>
              <div className="fc-people__by">{row.granted_by || DASH}</div>
              <div className="fc-people__act">
                {owner ? (
                  <>
                    <span className="fc-people__protected"
                      data-testid={`fc-access-owner-protected-${row.email}`}>
                      <Lock style={{ width: 12, height: 12 }} /> Protected
                    </span>
                    {bootstrap && (
                      <button className="fc-btn fc-btn--ghost" disabled={busy}
                        data-testid="fc-access-retire-bootstrap"
                        onClick={() => { setMessage(null); setConfirmRetire(true); }}>
                        Retire bootstrap
                      </button>
                    )}
                  </>
                ) : row.status === "revoked" ? (
                  <button className="fc-btn fc-btn--ghost" data-testid={`fc-access-view-${row.email}`}
                    onClick={() => { setMessage(null); setEditing(row.email); setEditRole(null); }}>
                    <Eye style={{ width: 13, height: 13 }} /> View
                  </button>
                ) : (
                  <>
                    <button className="fc-btn fc-btn--ghost" disabled={busy}
                      data-testid={`fc-access-edit-${row.email}`}
                      onClick={() => { setMessage(null); setEditing(row.email); setEditRole(row.role); }}>
                      <Pencil style={{ width: 13, height: 13 }} /> Edit access
                    </button>
                    <button className="fc-btn fc-btn--danger" disabled={busy}
                      data-testid={`fc-access-revoke-${row.email}`}
                      onClick={() => { setMessage(null); setRevoking(row.email); }}>
                      Revoke
                    </button>
                  </>
                )}
              </div>
              {owner && !bootstrap && row.status === "active" && (
                <p className="fc-people__owner-note">
                  Founder / Owner is protected — it cannot be assigned, changed or revoked here,
                  by anyone.
                </p>
              )}
            </div>
          );
        })}

        <div className="fc-people__foot">
          <span data-testid="fc-access-count">
            Showing {visible.length} of {filtered.length}
            {filtered.length === rows.length ? "" : ` (of ${rows.length})`} people
          </span>
          {pages > 1 && (
            <span className="fc-pager">
              <button className="fc-pager__btn" disabled={current === 0}
                data-testid="fc-access-prev" onClick={() => setPage(current - 1)}>
                <ChevronLeft style={{ width: 14, height: 14 }} />
              </button>
              <b data-testid="fc-access-page">{current + 1} / {pages}</b>
              <button className="fc-pager__btn" disabled={current >= pages - 1}
                data-testid="fc-access-next" onClick={() => setPage(current + 1)}>
                <ChevronRight style={{ width: 14, height: 14 }} />
              </button>
            </span>
          )}
        </div>

        {message && !adding && !editing && !revoking && (
          <div className="fc-grant__refusal" data-testid="fc-access-message">{message}</div>
        )}
        <Note testId="fc-access-domain-note">{data.notes?.domain}</Note>
        <Note testId="fc-access-ownership-note">{data.notes?.ownership}</Note>
        <Note testId="fc-access-content-note">{data.notes?.content}</Note>
        {data.bootstrap?.status === "active" && (
          <Note testId="fc-access-bootstrap-note">{data.bootstrap.note}</Note>
        )}
      </Panel>

      <button className="fc-drawer" data-testid="fc-roles-toggle"
        onClick={() => setShowRoles((v) => !v)}>
        <ShieldCheck style={{ width: 16, height: 16 }} />
        <span><b>Role quick reference</b><i>See exactly what each role can access.</i></span>
        <ChevronRight style={{ width: 16, height: 16 }} data-open={showRoles} />
      </button>
      {showRoles && (
        <Panel title="What each role may open" icon={ShieldCheck} testId="fc-roles-panel">
          <table className="fc-table">
            <thead><tr><th>Role</th><th>Exactly these permissions</th></tr></thead>
            <tbody>
              {Object.entries(data.roles).map(([name, spec]) => (
                <tr key={name} data-testid={`fc-role-${name}`}>
                  <td><b>{spec.label}</b>
                    <div style={{ fontSize: 11.5, color: "var(--fc-mute)", marginTop: 3 }}>
                      {spec.description}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--fc-mute)", marginTop: 3 }}>
                      {spec.assignable ? "Assignable here." : "Not assignable here."}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {spec.permissions.map((p) => (
                        <Chip key={p} tone="quiet" testId={`fc-role-${name}-${p}`}>{p}</Chip>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      <button className="fc-drawer" data-testid="fc-audit-toggle"
        onClick={() => setShowAudit((v) => !v)}>
        <History style={{ width: 16, height: 16 }} />
        <span><b>Access audit</b><i>The history of grants, changes and revocations.</i></span>
        <ChevronRight style={{ width: 16, height: 16 }} data-open={showAudit} />
      </button>
      {showAudit && (
        <Panel title="Access audit" icon={History} testId="fc-audit-panel"
          sub="Append-only. Nothing here can be edited or removed, by anyone.">
          <table className="fc-table">
            <thead><tr><th>When</th><th>Action</th><th>Identity</th><th>Role</th><th>By</th></tr></thead>
            <tbody>
              {(data.audit || []).map((row, i) => (
                <tr key={i} data-testid={`fc-audit-row-${i}`}>
                  <td style={{ fontSize: 12 }}>{(row.at || "").slice(0, 19).replace("T", " ")}</td>
                  <td><Chip tone={row.action === "revoked" ? "warn" : "quiet"}>{row.action}</Chip></td>
                  <td>{row.subject_email}</td>
                  <td>{row.role || DASH}</td>
                  <td style={{ fontSize: 12 }}>{row.actor_email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {adding && (
        <Modal title="Add Console access" testId="fc-access-add-modal"
          onClose={() => setAdding(false)}>
          <label className="fc-grant__label">Company email</label>
          <input
            className="fc-input" placeholder="name@…" value={email} autoFocus
            data-testid="fc-access-email" onChange={(e) => setEmail(e.target.value)}
          />
          <p className="fc-grant__hint" data-testid="fc-access-domain-chip">
            Must be an address on {domains.length ? domains.join(" or ") : "the approved company domain"}.
            Being on it grants nothing on its own — this authorization does.
          </p>

          <label className="fc-grant__label" style={{ marginTop: 18 }}>Role</label>
          {assignable.length ? (
            <RoleCards roles={data.roles} assignable={assignable} value={role} onPick={setRole}
              disabled={busy} testId="fc-access-new-role" />
          ) : (
            <p className="fc-grant__hint" data-testid="fc-access-no-roles">
              The server offered no assignable role, so nothing can be authorized.
            </p>
          )}
          <p className="fc-grant__hint" data-testid="fc-access-new-role-summary">
            {data.roles[role]?.description}
          </p>
          <Abilities permissions={data.permissions} granted={data.roles[role]?.permissions}
            testId="fc-access-new-role-abilities" />

          {message && <div className="fc-grant__refusal" data-testid="fc-access-message">{message}</div>}
          <div className="fc-modal__foot">
            <button className="fc-btn fc-btn--ghost" data-testid="fc-access-add-cancel"
              onClick={() => setAdding(false)}>Cancel</button>
            <button className="fc-btn" disabled={busy || !email.includes("@") || !role}
              data-testid="fc-access-grant"
              onClick={async () => {
                const ok = await act(() => axios.post(`${API}/access`, { email, role }));
                if (ok) { setAdding(false); setEmail(""); setQuery(""); setFilter("all"); }
              }}>
              <UserPlus style={{ width: 14, height: 14 }} /> Authorize
            </button>
          </div>
        </Modal>
      )}

      {editingRow && (
        <Modal title={editingRow.status === "revoked" ? "Access details" : "Edit Console access"}
          testId="fc-access-edit-modal" onClose={() => setEditing(null)}>
          <div className="fc-modal__who">
            <span className="fc-avatar">{initials(editingRow.name, editingRow.email)}</span>
            <span style={{ minWidth: 0 }}>
              <b>{editingRow.name || editingRow.email}</b>
              {editingRow.name ? <i>{editingRow.email}</i> : null}
            </span>
            <Chip tone={statusTone(editingRow.status)}
              testId={`fc-access-edit-status-${editingRow.email}`}>{editingRow.status}</Chip>
          </div>

          {editingRow.status === "revoked" ? (
            <>
              <p className="fc-grant__hint" style={{ marginTop: 14 }}>
                This identity holds no permissions and is refused at every Console request. It was
                last authorized as {data.roles[editingRow.role]?.label || editingRow.role}. To
                give it access again, authorize it through <b>Add person</b>.
              </p>
              <Abilities permissions={data.permissions} granted={[]}
                testId="fc-access-edit-abilities" />
            </>
          ) : (
            <>
              <label className="fc-grant__label" style={{ marginTop: 18 }}>Role</label>
              <RoleCards roles={data.roles} assignable={assignable}
                value={editRole || editingRow.role} onPick={setEditRole} disabled={busy}
                testId={`fc-access-role-${editingRow.email}`} />
              <label className="fc-grant__label" style={{ marginTop: 18 }}>
                This role can access
              </label>
              <Abilities permissions={data.permissions}
                granted={data.roles[editRole || editingRow.role]?.permissions}
                testId="fc-access-edit-abilities" />
            </>
          )}

          {message && <div className="fc-grant__refusal" data-testid="fc-access-message">{message}</div>}
          <div className="fc-modal__foot">
            <button className="fc-btn fc-btn--ghost" data-testid="fc-access-edit-cancel"
              onClick={() => setEditing(null)}>
              {editingRow.status === "revoked" ? "Close" : "Cancel"}
            </button>
            {editingRow.status !== "revoked" && (
              <button className="fc-btn" disabled={busy || !editRole || editRole === editingRow.role}
                data-testid={`fc-access-save-${editingRow.email}`}
                onClick={async () => {
                  const ok = await act(() => axios.patch(
                    `${API}/access/${encodeURIComponent(editingRow.email)}`,
                    { email: editingRow.email, role: editRole }));
                  if (ok) setEditing(null);
                }}>
                Save changes
              </button>
            )}
          </div>
        </Modal>
      )}

      {revokingRow && (
        <Modal title="Revoke access" testId="fc-access-revoke-modal"
          onClose={() => setRevoking(null)}>
          <div className="fc-modal__who">
            <span className="fc-avatar">{initials(revokingRow.name, revokingRow.email)}</span>
            <span style={{ minWidth: 0 }}>
              <b>{revokingRow.name || revokingRow.email}</b>
              {revokingRow.name ? <i>{revokingRow.email}</i> : null}
            </span>
          </div>
          <div className="fc-danger" data-testid="fc-access-revoke-warning">
            <b>Are you sure you want to revoke this person's Console access?</b>
            They will lose the Founder Console immediately, including any session they already have
            open. This will be recorded in the access audit.
          </div>
          {message && <div className="fc-grant__refusal" data-testid="fc-access-message">{message}</div>}
          <div className="fc-modal__foot">
            <button className="fc-btn fc-btn--ghost" data-testid={`fc-access-revoke-cancel-${revokingRow.email}`}
              onClick={() => setRevoking(null)}>Cancel</button>
            <button className="fc-btn fc-btn--danger" disabled={busy}
              data-testid={`fc-access-revoke-confirm-${revokingRow.email}`}
              onClick={async () => {
                const ok = await act(() => axios.delete(
                  `${API}/access/${encodeURIComponent(revokingRow.email)}`));
                if (ok) setRevoking(null);
              }}>
              <Trash2 style={{ width: 14, height: 14 }} /> Revoke access
            </button>
          </div>
        </Modal>
      )}

      {confirmRetire && (
        <Modal title="Retire bootstrap access" testId="fc-access-retire-modal"
          onClose={() => setConfirmRetire(false)}>
          <div className="fc-danger">
            <b>Retire {data.bootstrap?.email}?</b>
            This is the temporary identity that kept the Console reachable before the permanent
            Founder / Owner account existed. Its access ends immediately, it is recorded in the
            audit, and it cannot be recreated by a restart, a replay or a spoof.
          </div>
          {message && <div className="fc-grant__refusal" data-testid="fc-access-message">{message}</div>}
          <div className="fc-modal__foot">
            <button className="fc-btn fc-btn--ghost" data-testid="fc-access-retire-bootstrap-cancel"
              onClick={() => setConfirmRetire(false)}>Keep access</button>
            <button className="fc-btn fc-btn--danger" disabled={busy}
              data-testid="fc-access-retire-bootstrap-confirm"
              onClick={async () => {
                const ok = await act(() => axios.post(`${API}/access/retire-bootstrap`,
                  { confirm: data.bootstrap.email }));
                if (ok) setConfirmRetire(false);
              }}>
              Confirm retirement
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};
